import { MEMBER_ASSET_TYPE_IDS } from '@pokerroom/types';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import type {
  CreateMemberPointTypeDto,
  MemberPointTypeDto,
  UpdateMemberPointTypeDto,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { errorMessage } from './ledger/errorMessage';
import { BUTTON, INPUT, PANEL } from './ledger/walletShared';

export function PointTypeCatalog() {
  const scope = useAuthStore((s) =>
    JSON.stringify([
      s.user?.id,
      sessionVenueKey(s.accessToken),
      s.sessionRevision,
      s.allowedActions,
    ]),
  );
  return <ScopedPointTypeCatalog key={scope} />;
}
function ScopedPointTypeCatalog() {
  const actor = useAuthStore((s) => s.user?.id);
  const venue = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const revision = useAuthStore((s) => s.sessionRevision);
  const { t } = useTranslation('member');
  const canManage = useAuthStore((s) => s.allowedActions?.includes('member:asset_type_manage'));
  const canCreate = useAuthStore((s) => s.allowedActions?.includes('member:wallet_manage'));
  const { data, error } = useQuery<MemberPointTypeDto[]>({
    queryKey: ['point-types', actor, venue, revision],
    queryFn: () => api.get('/members/point-types'),
    retry: (count, err) => !(isAxiosError(err) && err.response?.status === 409) && count < 2,
  });
  if (isAxiosError(error) && error.response?.status === 409) return null;
  return (
    <section className={`${PANEL} mt-8`}>
      <h2 className="text-lg font-semibold">{t('pointTypesTitle')}</h2>
      {error && <p role="alert">{t('ledgerLoadFailed')}</p>}
      {canCreate && <CreatePointTypeForm />}
      <div className="space-y-4">
        {data
          ?.filter((type) => type.targetVenueId === venue)
          .map((type) => (
            <PointTypeRow key={type.id} type={type} canManage={!!canManage} />
          ))}
      </div>
    </section>
  );
}
function PointTypeRow({ type, canManage }: { type: MemberPointTypeDto; canManage: boolean }) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<UpdateMemberPointTypeDto | null>(null);
  const value = draft ?? type;
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: (input: { venue: boolean; body: object }) =>
      api.patch(`/members/point-types/${type.id}${input.venue ? '/venue' : ''}`, input.body),
    onSuccess: async () => {
      setDraft(null);
      setError('');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['point-types'] }),
        qc.invalidateQueries({ queryKey: ['member-ledger-summary'] }),
      ]);
    },
    onError: (err) => setError(errorMessage(err, t('ledgerLoadFailed'))),
  });
  const edit = (fields: Partial<UpdateMemberPointTypeDto>) =>
    setDraft({
      displayName: value.displayName,
      sortOrder: value.sortOrder,
      showOnCard: value.showOnCard,
      isActive: value.isActive,
      ...fields,
    });
  return (
    <form
      className="border border-base-300 rounded-lg p-3 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (draft) save.mutate({ venue: false, body: draft });
      }}
    >
      <p className="text-sm text-base-content/60">{type.displayName}</p>
      <p>{t(type.environmentAvailable ? 'pointTypeEnvironmentOn' : 'pointTypeEnvironmentOff')}</p>
      <p>{t(type.effectiveEnabled ? 'pointTypeEffectiveOn' : 'pointTypeEffectiveOff')}</p>
      <fieldset disabled={!canManage || save.isPending} className="grid gap-3 sm:grid-cols-2">
        <label>
          {t('pointTypeName')}
          <input
            className={INPUT}
            value={value.displayName}
            maxLength={100}
            required
            onChange={(e) => edit({ displayName: e.target.value })}
          />
        </label>
        <label>
          {t('pointTypeOrder')}
          <input
            className={INPUT}
            type="number"
            min={0}
            max={10000}
            value={value.sortOrder}
            onChange={(e) => edit({ sortOrder: Number(e.target.value) })}
          />
        </label>
        <label>
          <input
            type="checkbox"
            checked={value.showOnCard}
            onChange={(e) => edit({ showOnCard: e.target.checked })}
          />{' '}
          {t('pointTypeCard')}
        </label>
        <label>
          <input
            type="checkbox"
            checked={value.isActive}
            onChange={(e) => edit({ isActive: e.target.checked })}
          />{' '}
          {t('pointTypeActive')}
        </label>
        {canManage && (
          <button className={BUTTON} disabled={!draft || !value.displayName.trim()}>
            {t('ledgerSave')}
          </button>
        )}
      </fieldset>
      <label className="block">
        <input
          type="checkbox"
          disabled={!canManage || save.isPending}
          checked={
            type.venueOverride ?? Object.values(MEMBER_ASSET_TYPE_IDS).some((id) => id === type.id)
          }
          onChange={(e) => save.mutate({ venue: true, body: { enabled: e.target.checked } })}
        />{' '}
        {t('pointTypeVenue')}
      </label>
      <p className="text-sm text-base-content/60">
        {t(type.venueOverride == null ? 'pointTypeVenueDefault' : 'pointTypeVenueExplicit')}
      </p>
      <p className="text-sm text-base-content/60">{t('pointTypeGrantOnly')}</p>
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
    </form>
  );
}

function CreatePointTypeForm() {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [unit, setUnit] = useState<'WHOLE' | 'CENTS'>('WHOLE');
  const [expiry, setExpiry] = useState<'NONE' | 'PERIOD_NEXT_MONTH_END'>('NONE');
  const [error, setError] = useState('');
  const create = useMutation({
    mutationFn: (body: CreateMemberPointTypeDto) => api.post('/members/point-types', body),
    onSuccess: () => {
      setCode('');
      setName('');
      setError('');
      void qc.invalidateQueries({ queryKey: ['point-types'] });
    },
    onError: (failure) => setError(errorMessage(failure, t('ledgerLoadFailed'))),
  });
  return (
    <form
      className="my-4 space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate({
          code,
          displayName: name.trim(),
          sortOrder: 0,
          unit,
          expiryRule: expiry,
          negativeRule: 'NEVER',
          grantRule: 'NONE',
          showOnCard: true,
        });
      }}
    >
      <h3>{t('pointTypeCreate')}</h3>
      <label>
        {t('pointTypeCode')}
        <input
          className={INPUT}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          maxLength={32}
          pattern="[A-Z0-9_]+"
          required
        />
      </label>
      <label>
        {t('pointTypeName')}
        <input
          className={INPUT}
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={100}
          required
        />
      </label>
      <label>
        {t('pointTypeUnit')}
        <select
          className={INPUT}
          value={unit}
          onChange={(event) => {
            setUnit(event.target.value as 'WHOLE' | 'CENTS');
            setExpiry('NONE');
          }}
        >
          <option value="WHOLE">{t('pointTypeWhole')}</option>
          <option value="CENTS">{t('pointTypeDecimal')}</option>
        </select>
      </label>
      <label>
        {t('pointTypeExpiry')}
        <select
          className={INPUT}
          value={expiry}
          onChange={(event) => setExpiry(event.target.value as 'NONE' | 'PERIOD_NEXT_MONTH_END')}
        >
          <option value="NONE">{t('pointTypeNoExpiry')}</option>
          {unit === 'WHOLE' && (
            <option value="PERIOD_NEXT_MONTH_END">{t('pointTypeMonthExpiry')}</option>
          )}
        </select>
      </label>
      <p>{t('pointTypeStartsDisabled')}</p>
      <button className={BUTTON} disabled={create.isPending || !name.trim()}>
        {t('pointTypeCreate')}
      </button>
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
    </form>
  );
}
